import * as THREE from 'three';
import { Reflector } from 'three/addons/objects/Reflector.js';
import { applyWorldWallUvs, cloneMatWorldUv, uvRepeatForBrush, type ArenaMats, type CrateStyle } from './ArenaMaterials';
import type { BakeLight } from './BakeLights';

const DOOR_W = 1.15;
const INSET = 0.26;
const GRID = 1.2;
const YB = -3.5;
const YU = 3.6;

export type RoomSide = 'n' | 's' | 'e' | 'w';

export interface FacilityGap {
  side: RoomSide;
  along: number;
  width?: number;
}

export interface FacilityRoom {
  id: string;
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  floorY: number;
  height: number;
  gaps: FacilityGap[];
  sparse?: boolean;
  /** Extra occupancy volume (stairs, catwalks). Portal walls still use x0..x1. */
  walk?: { x0?: number; x1?: number; z0?: number; z1?: number };
  /** Always-visible rooms (stacked spaces that share a grate / hole). */
  see?: string[];
}

interface BarInst {
  x: number;
  y: number;
  z: number;
  length: number;
  yaw: number;
  roomId: string;
}

const _dir = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _dummy = new THREE.Object3D();
const _unitCyl = new THREE.CylinderGeometry(1, 1, 1, 10);
const _tankCyl = new THREE.CylinderGeometry(1, 1, 1, 20);
const _unitBox = new THREE.BoxGeometry(1, 1, 1);
const _torus = new THREE.TorusGeometry(0.16, 0.018, 8, 16);
const _mirrorPos = new THREE.Vector3();
const _mirrorN = new THREE.Vector3();
const _camFwd = new THREE.Vector3();
const _mirrorCamPos = new THREE.Vector3();
const _mirrorCamQuat = new THREE.Quaternion();
const _mirrorCamPosNow = new THREE.Vector3();
const _mirrorCamQuatNow = new THREE.Quaternion();
let _mirrorBusy = false;
let _mirrorLastGlobal = 0;
let _mirrorStagger = 0;
let _mirrorsLive = false;
let _mirrorForce = false;
let _mirrorCamFresh = false;
let _mirrorScenePrep: ((hide: boolean) => void) | null = null;
const _mirrorGlasses: Reflector[] = [];

const MIRROR_PREF = 'golden-mirror-quality';
export type MirrorQuality = 'high' | 'crap';

function loadMirrorQuality(): MirrorQuality {
  try {
    return localStorage.getItem(MIRROR_PREF) === 'high' ? 'high' : 'crap';
  } catch {
    return 'crap';
  }
}

let _mirrorQuality: MirrorQuality = loadMirrorQuality();

function mirrorRtSize(quality: MirrorQuality): number {
  return quality === 'high' ? 256 : 192;
}

function applyMirrorRtSize(): void {
  const size = mirrorRtSize(_mirrorQuality);
  for (const glass of _mirrorGlasses) {
    glass.getRenderTarget().setSize(size, size);
    glass.userData.mirrorLast = 0;
  }
  _mirrorLastGlobal = 0;
  _mirrorCamFresh = false;
}

function mirrorCameraStill(camera: THREE.Camera): boolean {
  if (!_mirrorCamFresh) return false;
  camera.getWorldPosition(_mirrorCamPosNow);
  camera.getWorldQuaternion(_mirrorCamQuatNow);
  if (_mirrorCamPosNow.distanceToSquared(_mirrorCamPos) > 0.0004) return false;
  return Math.abs(_mirrorCamQuatNow.dot(_mirrorCamQuat)) >= 0.9997;
}

function rememberMirrorCamera(camera: THREE.Camera): void {
  camera.getWorldPosition(_mirrorCamPos);
  camera.getWorldQuaternion(_mirrorCamQuat);
  _mirrorCamFresh = true;
}

export function getMirrorQuality(): MirrorQuality {
  return _mirrorQuality;
}

export function setMirrorQuality(quality: MirrorQuality): void {
  _mirrorQuality = quality;
  try {
    localStorage.setItem(MIRROR_PREF, quality);
  } catch {
    /* private mode */
  }
  applyMirrorRtSize();
}

export function setMirrorsLive(live: boolean): void {
  _mirrorsLive = live;
}

/** Hide loft-only meshes for the duration of a Reflector pass. */
export function setMirrorScenePrep(fn: ((hide: boolean) => void) | null): void {
  _mirrorScenePrep = fn;
}

export function mirrorLastAgeMs(): number {
  return _mirrorLastGlobal > 0 ? performance.now() - _mirrorLastGlobal : -1;
}

/** Force the next glance to re-render (weapon just equipped, etc). */
export function dirtyMirrors(): void {
  _mirrorLastGlobal = 0;
  _mirrorCamFresh = false;
  for (const glass of _mirrorGlasses) glass.userData.mirrorLast = 0;
}

/** One offscreen-style pass so the first glance does not compile shaders mid-frame. */
export function warmMirrors(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
): void {
  const prevLive = _mirrorsLive;
  _mirrorsLive = true;
  _mirrorForce = true;
  _mirrorLastGlobal = 0;
  try {
    renderer.clear();
    renderer.render(scene, camera);
  } finally {
    _mirrorForce = false;
    _mirrorsLive = prevLive;
  }
}

/**
 * Visual-only architecture + set dressing. Does not add collision or cover brushes.
 */
export function dressFacility(group: THREE.Group, mats: ArenaMats, rooms: FacilityRoom[]): BakeLight[] {
  const root = new THREE.Group();
  root.name = 'facility_dressing';
  group.add(root);

  const skirtBars: BarInst[] = [];
  const corniceBars: BarInst[] = [];
  const gridBars: BarInst[] = [];
  const trimSparse = new Set(['stairup', 'stairdn', 'gen', 'lockers', 'lockern', 'stash_s', 'stash_w']);
  const gridSparse = new Set(['lockers', 'lockern', 'gen', 'stash_s', 'stash_w']);

  for (const room of rooms) {
    if (room.sparse && !trimSparse.has(room.id)) continue;
    collectRoomTrim(room, skirtBars, corniceBars);
    if (!room.sparse || gridSparse.has(room.id)) collectCeilingGrid(room, gridBars);
  }

  flushBars(root, skirtBars, mats.metal, 0.1, 0.055, 'skirt', true);
  flushBars(root, corniceBars, mats.metal, 0.07, 0.065, 'cornice', false);
  flushBars(root, gridBars, mats.ceiling, 0.028, 0.038, 'ceil_grid', false);

  addWainscot(root, mats, rooms);
  addCorridorPanels(root, mats, rooms);
  addPipes(root, mats);
  addFloorStripes(root, mats);
  addJambBands(root, mats);
  addSigns(root);
  addPosters(root);
  addStains(root, mats);
  addTanksAndSilos(root, mats);
  addBathroom(root, mats);
  const bake = addFoyerNeon(root);
  addDesks(root, mats);
  addLabDressing(root, mats);
  addBottling(root, mats);
  addCrateStacks(root, mats);
  addKitchenDressing(root, mats);
  addGeneratorDressing(root, mats);
  addStashDressing(root, mats);
  addLockerDressing(root, mats);
  addAlarmButtons(root, mats);
  return bake;
}

function splitRange(min: number, max: number, holes: Array<{ along: number; half: number }>): Array<[number, number]> {
  const cuts = holes.map((h) => [h.along - h.half, h.along + h.half] as const).sort((a, b) => a[0] - b[0]);
  const segs: Array<[number, number]> = [];
  let t = min;
  for (const [a, b] of cuts) {
    const hi = Math.min(a, max);
    if (hi > t + 0.1) segs.push([t, hi]);
    t = Math.max(t, b);
  }
  if (max > t + 0.1) segs.push([t, max]);
  return segs;
}

function gapsOn(room: FacilityRoom, side: RoomSide): Array<{ along: number; half: number }> {
  return room.gaps
    .filter((g) => g.side === side)
    .map((g) => ({ along: g.along, half: (g.width ?? DOOR_W) * 0.5 + 0.1 }));
}

function collectRoomTrim(room: FacilityRoom, skirt: BarInst[], cornice: BarInst[]): void {
  const x0 = room.x0 + INSET;
  const x1 = room.x1 - INSET;
  const z0 = room.z0 + INSET;
  const z1 = room.z1 - INSET;
  const skirtY = room.floorY + 0.1;
  const corniceY = room.floorY + room.height - 0.1;

  const push = (list: BarInst[], y: number, ax: number, az: number, bx: number, bz: number) => {
    const dx = bx - ax;
    const dz = bz - az;
    const len = Math.hypot(dx, dz);
    if (len < 0.12) return;
    list.push({
      x: (ax + bx) * 0.5,
      y,
      z: (az + bz) * 0.5,
      length: len,
      yaw: Math.atan2(dx, dz) - Math.PI * 0.5,
      roomId: room.id,
    });
  };

  const run = (
    list: BarInst[],
    y: number,
    holes: { s: ReturnType<typeof gapsOn>; n: ReturnType<typeof gapsOn>; w: ReturnType<typeof gapsOn>; e: ReturnType<typeof gapsOn> },
  ) => {
    for (const [a, b] of splitRange(x0, x1, holes.s)) push(list, y, a, z0, b, z0);
    for (const [a, b] of splitRange(x0, x1, holes.n)) push(list, y, a, z1, b, z1);
    for (const [a, b] of splitRange(z0, z1, holes.w)) push(list, y, x0, a, x0, b);
    for (const [a, b] of splitRange(z0, z1, holes.e)) push(list, y, x1, a, x1, b);
  };
  const doorGaps = {
    s: gapsOn(room, 's'),
    n: gapsOn(room, 'n'),
    w: gapsOn(room, 'w'),
    e: gapsOn(room, 'e'),
  };
  const none = { s: [], n: [], w: [], e: [] };
  run(skirt, skirtY, doorGaps);
  // Cornice sits above the door — keep it continuous so the ceiling grid does not break.
  run(cornice, corniceY, none);
}

function collectCeilingGrid(room: FacilityRoom, bars: BarInst[]): void {
  if (room.id === 'bath') return;
  const x0 = room.x0 + 0.35;
  const x1 = room.x1 - 0.35;
  const z0 = room.z0 + 0.35;
  const z1 = room.z1 - 0.35;
  const sx = x1 - x0;
  const sz = z1 - z0;
  if (sx < 0.8 || sz < 0.8) return;
  const gridY = room.floorY + room.height - 0.16;

  for (let x = x0 + GRID * 0.5; x < x1 - 0.15; x += GRID) {
    bars.push({ x, y: gridY, z: (z0 + z1) * 0.5, length: sz, yaw: Math.PI * 0.5, roomId: room.id });
  }
  for (let z = z0 + GRID * 0.5; z < z1 - 0.15; z += GRID) {
    bars.push({ x: (x0 + x1) * 0.5, y: gridY, z, length: sx, yaw: 0, roomId: room.id });
  }
}

function flushBars(
  parent: THREE.Object3D,
  items: BarInst[],
  mat: THREE.Material,
  hy: number,
  hz: number,
  name: string,
  receive: boolean,
): void {
  if (items.length === 0) return;
  const byRoom = new Map<string, BarInst[]>();
  for (const it of items) {
    let list = byRoom.get(it.roomId);
    if (!list) {
      list = [];
      byRoom.set(it.roomId, list);
    }
    list.push(it);
  }
  const geo = new THREE.BoxGeometry(1, hy, hz);
  for (const [roomId, list] of byRoom) {
    const mesh = new THREE.InstancedMesh(geo, mat, list.length);
    mesh.name = `${name}_${roomId}`;
    mesh.userData.rooms = [roomId];
    mesh.castShadow = false;
    mesh.receiveShadow = receive;
    mesh.frustumCulled = true;
    for (let i = 0; i < list.length; i++) {
      const it = list[i]!;
      _dummy.position.set(it.x, it.y, it.z);
      _dummy.rotation.set(0, it.yaw, 0);
      _dummy.scale.set(it.length, 1, 1);
      _dummy.updateMatrix();
      mesh.setMatrixAt(i, _dummy.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();
    mesh.raycast = () => {};
    parent.add(mesh);
  }
}

function addWallBand(
  parent: THREE.Object3D,
  room: FacilityRoom,
  mat: THREE.MeshStandardMaterial,
  h: number,
  y0: number,
  t = 0.035,
  inset = 0.23,
): void {
  const y = room.floorY + y0 + h * 0.5;
  const x0 = room.x0 + inset;
  const x1 = room.x1 - inset;
  const z0 = room.z0 + inset;
  const z1 = room.z1 - inset;

  const panel = (x: number, z: number, sx: number, sz: number) => {
    if (sx < 0.2 || sz < 0.2) return;
    const uv = uvRepeatForBrush(new THREE.Vector3(sx, h, sz), 'wall');
    const m = new THREE.Mesh(_unitBox, cloneMatWorldUv(mat, uv.u, uv.v));
    m.position.set(x, y, z);
    m.scale.set(sx, h, sz);
    m.receiveShadow = true;
    parent.add(m);
  };

  for (const [a, b] of splitRange(x0, x1, gapsOn(room, 's'))) panel((a + b) * 0.5, z0, b - a, t);
  for (const [a, b] of splitRange(x0, x1, gapsOn(room, 'n'))) panel((a + b) * 0.5, z1, b - a, t);
  for (const [a, b] of splitRange(z0, z1, gapsOn(room, 'w'))) panel(x0, (a + b) * 0.5, t, b - a);
  for (const [a, b] of splitRange(z0, z1, gapsOn(room, 'e'))) panel(x1, (a + b) * 0.5, t, b - a);
}

function addWainscot(parent: THREE.Object3D, mats: ArenaMats, rooms: FacilityRoom[]): void {
  const bath = rooms.find((r) => r.id === 'bath');
  if (bath) addWallBand(parent, bath, mats.brick, 1.08, 0);
  const hub = rooms.find((r) => r.id === 'hub');
  if (hub) addWallBand(parent, hub, mats.metal, 0.94, 0);
  const sec = rooms.find((r) => r.id === 'sec');
  if (sec) addWallBand(parent, sec, mats.metal, 0.94, 0);
  const wing = rooms.find((r) => r.id === 'wing');
  if (wing) addWallBand(parent, wing, mats.metal, 0.94, 0);
}

function addCorridorPanels(parent: THREE.Object3D, mats: ArenaMats, rooms: FacilityRoom[]): void {
  const hub = rooms.find((r) => r.id === 'hub');
  if (hub) addWallBand(parent, hub, mats.wallSec, 0.42, 1.58, 0.028, 0.25);
  const sec = rooms.find((r) => r.id === 'sec');
  if (sec) addWallBand(parent, sec, mats.wallSec, 0.42, 1.58, 0.028, 0.25);
  const wing = rooms.find((r) => r.id === 'wing');
  if (wing) addWallBand(parent, wing, mats.wallSec, 0.42, 1.58, 0.028, 0.25);
}

function addPipe(
  parent: THREE.Object3D,
  mat: THREE.Material,
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  r: number,
): void {
  _dir.set(bx - ax, by - ay, bz - az);
  const len = _dir.length();
  if (len < 0.05) return;
  _dir.multiplyScalar(1 / len);
  const mesh = new THREE.Mesh(_unitCyl, mat);
  mesh.position.set((ax + bx) * 0.5, (ay + by) * 0.5, (az + bz) * 0.5);
  mesh.scale.set(r, len, r);
  mesh.quaternion.setFromUnitVectors(_up, _dir);
  mesh.castShadow = true;
  parent.add(mesh);
}

function addHanger(parent: THREE.Object3D, mat: THREE.Material, x: number, y: number, z: number): void {
  const m = new THREE.Mesh(_unitBox, mat);
  m.position.set(x, y, z);
  m.scale.set(0.08, 0.12, 0.04);
  parent.add(m);
}

function addPipes(parent: THREE.Object3D, mats: ArenaMats): void {
  const p = mats.pipe;
  const rust = mats.pipeRust;

  addPipe(parent, p, 4.4, 2.82, 12.4, 4.4, 2.82, 20.4, 0.045);
  for (let z = 14; z <= 20; z += 3) addHanger(parent, mats.metal, 4.4, 2.92, z);

  addPipe(parent, p, -8.6, 2.82, 36.5, -8.6, 2.82, 50.2, 0.05);
  addPipe(parent, rust, -8.4, 2.72, 36.5, -8.4, 2.72, 50.2, 0.028);

  addPipe(parent, p, -21.2, YB + 2.88, 22.5, -21.2, YB + 2.88, 40.5, 0.07);
  addPipe(parent, p, -21.2, YB + 2.88, 28, -16.5, YB + 2.88, 28, 0.055);
  addPipe(parent, p, -16.5, YB + 2.88, 28, -16.5, YB + 2.55, 28, 0.055);
  addPipe(parent, p, -21.2, YB + 2.88, 35, -12.8, YB + 2.88, 35, 0.055);
  addPipe(parent, p, -12.8, YB + 2.88, 35, -12.8, YB + 2.55, 35, 0.055);

  addPipe(parent, p, 19.4, 2.82, 22.4, 19.4, 2.82, 33.6, 0.045);
  for (let z = 24; z <= 33; z += 3) addHanger(parent, mats.metal, 19.4, 2.92, z);

  addPipe(parent, p, 17.4, YU + 2.82, 36.5, 17.4, YU + 2.82, 54.5, 0.045);
  addPipe(parent, rust, 17.55, YU + 2.72, 36.5, 17.55, YU + 2.72, 54.5, 0.028);

  addPipe(parent, p, -10.4, 6.7, 53.5, -10.4, 6.7, 72.5, 0.08);
  addPipe(parent, p, 10.4, 6.7, 53.5, 10.4, 6.7, 72.5, 0.08);
  addPipe(parent, p, -10.4, 6.7, 62, -5, 6.7, 62, 0.06);
  addPipe(parent, p, -5, 6.7, 62, -5, 2.85, 62, 0.06);
  addPipe(parent, p, 10.4, 6.7, 62, 5, 6.7, 62, 0.06);
  addPipe(parent, p, 5, 6.7, 62, 5, 2.85, 62, 0.06);
  addPipe(parent, p, 0, 6.7, 69.5, 0, 2.85, 69.5, 0.07);

  addPipe(parent, p, 22.2, 2.88, 36.2, 22.2, 2.88, 50.2, 0.05);
  addPipe(parent, rust, 22.4, 2.78, 36.2, 22.4, 2.78, 50.2, 0.028);
  for (let z = 38; z <= 50; z += 4) addHanger(parent, mats.metal, 22.2, 2.98, z);
  addPipe(parent, p, 22.2, 2.88, 43, 30.5, 2.88, 43, 0.045);

  addPipe(parent, p, -11.4, YU + 0.12, 53.5, -11.4, YU + 0.12, 72.4, 0.06);
  addPipe(parent, rust, -11.2, YU + 0.02, 53.5, -11.2, YU + 0.02, 72.4, 0.032);
  addPipe(parent, p, 11.4, YU + 0.12, 53.5, 11.4, YU + 0.12, 72.4, 0.06);
}

function addStripe(
  parent: THREE.Object3D,
  mats: ArenaMats,
  x: number,
  z: number,
  sx: number,
  sz: number,
  y = 0.012,
): void {
  const n = Math.max(2, Math.round(Math.max(sx, sz) / 0.32));
  const alongX = sx >= sz;
  const total = alongX ? sx : sz;
  const seg = total / n;
  for (let i = 0; i < n; i++) {
    const mat = i % 2 === 0 ? mats.hazard : mats.hazardBlack;
    const m = new THREE.Mesh(_unitBox, mat);
    const o = -total * 0.5 + (i + 0.5) * seg;
    m.position.set(x + (alongX ? o : 0), y, z + (alongX ? 0 : o));
    m.scale.set(alongX ? seg * 0.98 : sx, 0.012, alongX ? sz : seg * 0.98);
    parent.add(m);
  }
}

function addFloorStripes(parent: THREE.Object3D, mats: ArenaMats): void {
  addStripe(parent, mats, 0, 28, 0.32, 10);
  addStripe(parent, mats, 14.5, 28, 0.32, 8);
  addStripe(parent, mats, 26, 28, 0.32, 8);
  addStripe(parent, mats, -11, 7, 0.28, 6, YB + 0.012);
  addStripe(parent, mats, -19, 12, 0.28, 8, YB + 0.012);
  addStripe(parent, mats, 0, 43, 0.32, 12);
  addStripe(parent, mats, -15.5, 31, 0.32, 12, YB + 0.012);
  addStripe(parent, mats, 0, 60, 0.36, 10);
  for (const [cx, cz, s] of [
    [-5, 62, 2.8],
    [5, 62, 2.8],
    [0, 69.5, 2.6],
  ] as const) {
    addStripe(parent, mats, cx, cz - s * 0.5, s, 0.18);
    addStripe(parent, mats, cx, cz + s * 0.5, s, 0.18);
    addStripe(parent, mats, cx - s * 0.5, cz, 0.18, s * 0.7);
    addStripe(parent, mats, cx + s * 0.5, cz, 0.18, s * 0.7);
  }
}

function addJambBands(parent: THREE.Object3D, mats: ArenaMats): void {
  // Sit on the lintel faces, above the metal header (y 2.25–2.32) and door leaf.
  const doors: Array<{ x: number; z: number; alongX: boolean; y: number }> = [
    { x: 0, z: 11, alongX: true, y: 2.4 },
    { x: 0, z: 21, alongX: true, y: 2.4 },
    { x: 0, z: 35, alongX: true, y: 2.4 },
    { x: 0, z: 51, alongX: true, y: 2.4 },
    { x: 9, z: 28, alongX: false, y: 2.4 },
    { x: 13, z: 35, alongX: true, y: 2.4 },
    { x: 20, z: 28, alongX: false, y: 2.4 },
    { x: 26, z: 35.42, alongX: true, y: 2.4 },
    { x: 15.55, z: 38, alongX: false, y: 2.4 },
    { x: 15.55, z: 48, alongX: false, y: 2.4 },
    { x: 21, z: 38, alongX: false, y: 2.4 },
    { x: 21, z: 48, alongX: false, y: 2.4 },
    { x: 21, z: 36.15, alongX: false, y: YU + 2.4 },
    { x: 21, z: 47.55, alongX: false, y: YU + 2.4 },
    { x: 21, z: 50.35, alongX: false, y: YU + 2.4 },
    { x: -11.2, z: 11, alongX: true, y: YB + 2.4 },
    { x: -16, z: 13.4, alongX: false, y: YB + 2.4 },
  ];
  const face = 0.22;
  const plateT = 0.03;
  const bandH = 0.07;
  for (const d of doors) {
    const w = DOOR_W + 0.16;
    for (const sign of [-1, 1] as const) {
      const m = new THREE.Mesh(_unitBox, mats.hazard);
      if (d.alongX) {
        m.position.set(d.x, d.y, d.z + sign * face);
        m.scale.set(w, bandH, plateT);
      } else {
        m.position.set(d.x + sign * face, d.y, d.z);
        m.scale.set(plateT, bandH, w);
      }
      parent.add(m);
    }
  }
}

function makeSignTexture(title: string, sub: string): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 256;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#1a221c';
  ctx.fillRect(0, 0, 512, 256);
  ctx.strokeStyle = '#c9a020';
  ctx.lineWidth = 10;
  ctx.strokeRect(14, 14, 484, 228);
  ctx.fillStyle = '#e8d070';
  ctx.textAlign = 'center';
  ctx.font = 'bold 48px sans-serif';
  ctx.fillText(title, 256, 118);
  ctx.fillStyle = '#c8c0a8';
  ctx.font = '22px sans-serif';
  ctx.fillText(sub, 256, 168);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

function addSign(
  parent: THREE.Object3D,
  title: string,
  sub: string,
  x: number,
  y: number,
  z: number,
  yaw: number,
): void {
  const mat = new THREE.MeshStandardMaterial({
    map: makeSignTexture(title, sub),
    roughness: 0.55,
    metalness: 0.15,
  });
  const plate = new THREE.Mesh(new THREE.PlaneGeometry(0.95, 0.48), mat);
  plate.position.set(x, y, z);
  plate.rotation.y = yaw;
  parent.add(plate);
}

function addSigns(parent: THREE.Object3D): void {
  addSign(parent, 'SECURITY', 'AUTHORIZED AREA', 3.4, 1.85, 21.23, 0);
  addSign(parent, 'EAST WING', 'SECURITY ANNEX', 8.76, 1.85, 26.3, -Math.PI * 0.5);
  addSign(parent, 'CHEMICAL', 'STORAGE — TANK BAY', -21.76, YB + 1.9, 30, Math.PI * 0.5);
  addSign(parent, 'LABORATORY', 'LEVEL 2 ACCESS', 9.76, 1.85, 40.5, -Math.PI * 0.5);
  addSign(parent, 'LABORATORY', 'BAY ACCESS', 24.76, YU + 1.85, 38.2, -Math.PI * 0.5);
  addSign(parent, 'BOTTLING', 'HEAVY MACHINERY', 3.4, 1.85, 50.76, Math.PI);
  addSign(parent, 'KITCHEN', 'STAFF ONLY', 20.24, 1.85, 26.2, -Math.PI * 0.5);
  addSign(parent, 'GENERATOR', 'AUTHORIZED PERSONNEL', 26.2, 1.85, 35.64, 0);
  addSign(parent, 'STOREROOM', 'AUTHORIZED PERSONNEL', -11.2, YB + 2.52, 11.24, 0);
  addSign(parent, 'LOCKERS', 'PERSONNEL ONLY', 15.78, 2.52, 38.0, Math.PI * 0.5);
  addSign(parent, 'LOCKERS', 'SHIFT B', 15.78, 2.52, 48.0, Math.PI * 0.5);
  addSign(parent, 'BASEMENT', 'TANK STORAGE', -6.28, YB + 1.85, 16.0, -Math.PI * 0.5);
}

type PosterTheme = 'hazard' | 'rad' | 'auth' | 'safety' | 'notice';

function posterPalette(theme: PosterTheme): { bg: string; ink: string; edge: string; inner: string } {
  if (theme === 'auth') return { bg: '#4a1410', ink: '#f0c8b0', edge: '#c04028', inner: '#8a2018' };
  if (theme === 'rad') return { bg: '#3a3010', ink: '#f2e080', edge: '#c9a020', inner: '#1a1a12' };
  if (theme === 'safety') return { bg: '#14301c', ink: '#b8e0c0', edge: '#3a8a50', inner: '#0c1c12' };
  if (theme === 'notice') return { bg: '#1a2438', ink: '#c8d8f0', edge: '#4a6a9a', inner: '#101820' };
  return { bg: '#2a2610', ink: '#f2e080', edge: '#c9a020', inner: '#1a1a12' };
}

function makePosterTexture(title: string, sub: string, theme: PosterTheme): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 384;
  c.height = 512;
  const ctx = c.getContext('2d')!;
  const { bg, ink, edge, inner } = posterPalette(theme);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, 384, 512);
  ctx.strokeStyle = edge;
  ctx.lineWidth = 14;
  ctx.strokeRect(16, 16, 352, 480);
  ctx.strokeStyle = inner;
  ctx.lineWidth = 6;
  ctx.strokeRect(28, 28, 328, 456);
  ctx.fillStyle = edge;
  ctx.beginPath();
  ctx.moveTo(192, 72);
  ctx.lineTo(248, 168);
  ctx.lineTo(136, 168);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#1a1408';
  ctx.font = 'bold 64px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('!', 192, 150);
  ctx.fillStyle = ink;
  ctx.font = 'bold 36px sans-serif';
  ctx.fillText(title, 192, 260);
  ctx.fillStyle = '#d0c8a8';
  ctx.font = '22px sans-serif';
  ctx.fillText(sub, 192, 320);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

function addPoster(
  parent: THREE.Object3D,
  title: string,
  sub: string,
  theme: PosterTheme,
  x: number,
  y: number,
  z: number,
  yaw: number,
): void {
  const mat = new THREE.MeshStandardMaterial({
    map: makePosterTexture(title, sub, theme),
    roughness: 0.72,
    metalness: 0.04,
  });
  const plate = new THREE.Mesh(new THREE.PlaneGeometry(0.62, 0.82), mat);
  plate.position.set(x, y, z);
  plate.rotation.y = yaw;
  parent.add(plate);
}

function addPosters(parent: THREE.Object3D): void {
  addPoster(parent, 'HAZARD', 'CHEMICAL RISK', 'hazard', -21.76, YB + 1.55, 24.2, Math.PI * 0.5);
  addPoster(parent, 'RADIATION', 'RESTRICTED', 'rad', 16.28, YU + 1.65, 39.4, Math.PI * 0.5);
  addPoster(parent, 'AUTHORIZED', 'NO ENTRY', 'auth', 31.72, 1.65, 42.2, -Math.PI * 0.5);
  addPoster(parent, 'HAZARD', 'HIGH VOLTAGE', 'hazard', 21.28, 1.65, 43.6, Math.PI * 0.5);
  addPoster(parent, 'SAFETY', 'WASH HANDS', 'safety', 31.72, 1.65, 29.2, -Math.PI * 0.5);
  addPoster(parent, 'PERSONNEL', 'GEAR UP', 'notice', 16.02, 1.55, 40.2, Math.PI * 0.5);
  addPoster(parent, 'KEEP OUT', 'STOREROOM', 'auth', -6.28, YB + 1.55, 8.2, -Math.PI * 0.5);
  addPoster(parent, 'VOLTAGE', 'GENERATOR BAY', 'hazard', 31.72, 1.65, 47.8, -Math.PI * 0.5);
  addPoster(parent, 'HAZARD', 'HEAVY LOAD', 'hazard', -11.76, 1.65, 58.4, Math.PI * 0.5);
  addPoster(parent, 'NOTICE', 'TANK WATCH', 'notice', -21.76, YB + 1.55, 38.0, Math.PI * 0.5);
  addPoster(parent, 'SAFETY', 'MIND THE STEP', 'safety', 9.24, 1.65, 24.6, Math.PI * 0.5);
}

function addStain(
  parent: THREE.Object3D,
  mats: ArenaMats,
  x: number,
  y: number,
  z: number,
  sx: number,
  sz: number,
  yaw = 0,
): void {
  const m = new THREE.Mesh(_unitBox, cloneMatWorldUv(mats.stain, Math.max(0.6, sx), Math.max(0.6, sz)));
  m.position.set(x, y, z);
  m.rotation.y = yaw;
  m.scale.set(sx, 0.01, sz);
  m.receiveShadow = true;
  parent.add(m);
}

function addStains(parent: THREE.Object3D, mats: ArenaMats): void {
  addStain(parent, mats, 4.4, 0.014, 16.2, 0.7, 1.6);
  addStain(parent, mats, -16.5, YB + 0.014, 28, 1.5, 1.4);
  addStain(parent, mats, -12.8, YB + 0.014, 35, 1.5, 1.4);
  addStain(parent, mats, -21.0, YB + 0.014, 28, 1.1, 0.9);
  addStain(parent, mats, -5, 0.014, 62, 1.8, 1.8);
  addStain(parent, mats, 5, 0.014, 62, 1.8, 1.8);
  addStain(parent, mats, 0, 0.014, 69.5, 1.6, 1.6);
  addStain(parent, mats, 27.2, 0.014, 30.4, 1.2, 0.9);
  addStain(parent, mats, 26.5, 0.014, 44.0, 1.4, 1.1);
}

function addTankVisual(
  parent: THREE.Object3D,
  mats: ArenaMats,
  x: number,
  y: number,
  z: number,
  radius: number,
  height: number,
  silo: boolean,
): void {
  const steel = cloneMatWorldUv(mats.tankMetal, 2.6, 1.8);
  const rim = cloneMatWorldUv(mats.tankMetal, 1.6, 0.45);
  const body = new THREE.Mesh(_tankCyl, steel);
  body.position.set(x, y, z);
  body.scale.set(radius, height, radius);
  body.castShadow = true;
  body.receiveShadow = true;
  parent.add(body);

  const flange = new THREE.Mesh(_tankCyl, rim);
  flange.position.set(x, y + height * 0.5 - 0.04, z);
  flange.scale.set(radius + 0.06, 0.08, radius + 0.06);
  parent.add(flange);

  const skirt = new THREE.Mesh(_tankCyl, rim);
  skirt.position.set(x, y - height * 0.5 + 0.06, z);
  skirt.scale.set(radius + 0.05, 0.12, radius + 0.05);
  parent.add(skirt);

  for (const t of silo ? [0.22, 0.5, 0.78] : [0.28, 0.62]) {
    const band = new THREE.Mesh(_unitCyl, mats.hazardBlack);
    band.position.set(x, y - height * 0.5 + height * t, z);
    band.scale.set(radius + 0.02, 0.045, radius + 0.02);
    parent.add(band);
  }

  const valve = new THREE.Mesh(_torus, mats.hazard);
  valve.position.set(x + radius * 0.92, y - height * 0.12, z);
  valve.rotation.y = Math.PI * 0.5;
  parent.add(valve);

  if (silo) {
    const cap = new THREE.Mesh(new THREE.ConeGeometry(radius * 0.92, 0.28, 12), steel);
    cap.position.set(x, y + height * 0.5 + 0.12, z);
    parent.add(cap);
  }
}

function addTanksAndSilos(parent: THREE.Object3D, mats: ArenaMats): void {
  addTankVisual(parent, mats, -16.5, YB + 1.35, 28, 0.72, 2.7, false);
  addTankVisual(parent, mats, -12.8, YB + 1.35, 35, 0.72, 2.7, false);
  addTankVisual(parent, mats, -5, 1.5, 62, 1.02, 3.0, true);
  addTankVisual(parent, mats, 5, 1.5, 62, 1.02, 3.0, true);
  addTankVisual(parent, mats, 0, 1.5, 69.5, 0.92, 3.0, true);
}

const TOILET_PORCELAIN = new THREE.MeshStandardMaterial({
  color: 0xe8e4dc,
  roughness: 0.22,
  metalness: 0.04,
});
const TOILET_SEAT = new THREE.MeshStandardMaterial({
  color: 0xd4d0c8,
  roughness: 0.38,
  metalness: 0.02,
});
const TOILET_CHROME = new THREE.MeshStandardMaterial({
  color: 0xc8d0d6,
  roughness: 0.28,
  metalness: 0.78,
});
const TOILET_RIM_GEO = new THREE.TorusGeometry(0.13, 0.028, 8, 18);
const TOILET_SEAT_GEO = new THREE.TorusGeometry(0.125, 0.022, 8, 16);

const SINK_PORCELAIN = new THREE.MeshStandardMaterial({
  color: 0xece8e0,
  roughness: 0.2,
  metalness: 0.05,
  side: THREE.DoubleSide,
});
const SINK_CHROME = new THREE.MeshStandardMaterial({
  color: 0xd4dce2,
  roughness: 0.16,
  metalness: 0.92,
});
const SINK_DRAIN = new THREE.MeshStandardMaterial({
  color: 0x2a3034,
  roughness: 0.35,
  metalness: 0.7,
});
const MIRROR_FRAME = new THREE.MeshStandardMaterial({
  color: 0xb8c2c8,
  roughness: 0.28,
  metalness: 0.82,
});
const STALL_PANEL = new THREE.MeshStandardMaterial({
  color: 0x6a7280,
  roughness: 0.52,
  metalness: 0.28,
});

function addToilet(parent: THREE.Object3D, _mats: ArenaMats, x: number, z: number): void {
  const g = new THREE.Group();
  g.name = 'toilet';
  g.position.set(x, 0, z);
  g.userData.destructible = 'toilet';
  g.userData.interact = 'toilet';
  g.userData.sprayOrigin = new THREE.Vector3(0, 0.4, 0.05);

  const porcelain = TOILET_PORCELAIN;
  const seatMat = TOILET_SEAT;
  const chrome = TOILET_CHROME;

  const tank = new THREE.Mesh(_unitBox, porcelain);
  tank.position.set(0, 0.74, -0.24);
  tank.scale.set(0.38, 0.4, 0.15);
  const bowl = new THREE.Mesh(_unitCyl, porcelain);
  bowl.position.set(0, 0.2, 0.05);
  bowl.scale.set(0.155, 0.22, 0.18);
  const rim = new THREE.Mesh(TOILET_RIM_GEO, porcelain);
  rim.position.set(0, 0.36, 0.05);
  rim.rotation.x = Math.PI * 0.5;
  const seat = new THREE.Mesh(TOILET_SEAT_GEO, seatMat);
  seat.position.set(0, 0.385, 0.05);
  seat.rotation.x = Math.PI * 0.5;
  const lid = new THREE.Mesh(_unitBox, seatMat);
  lid.name = 'toilet_lid';
  lid.position.set(0, 0.41, 0.02);
  lid.scale.set(0.3, 0.018, 0.34);
  const lever = new THREE.Mesh(_unitBox, chrome);
  lever.position.set(0.14, 0.86, -0.16);
  lever.scale.set(0.07, 0.012, 0.012);
  g.add(tank, bowl, rim, seat, lid, lever);
  g.userData.lid = lid;
  g.userData.lever = lever;
  g.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh) {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
    }
  });
  parent.add(g);
}

function porcelainMat(): THREE.MeshStandardMaterial {
  return SINK_PORCELAIN;
}

function chromeMat(): THREE.MeshStandardMaterial {
  return SINK_CHROME;
}

let _sinkBowlGeo: THREE.LatheGeometry | null = null;
function sinkBowlGeo(): THREE.LatheGeometry {
  if (_sinkBowlGeo) return _sinkBowlGeo;
  _sinkBowlGeo = makeSinkBowlGeo();
  return _sinkBowlGeo;
}

function makeSinkBowlGeo(): THREE.LatheGeometry {
  const pts = [
    new THREE.Vector2(0.02, 0.0),
    new THREE.Vector2(0.048, 0.014),
    new THREE.Vector2(0.11, 0.04),
    new THREE.Vector2(0.155, 0.09),
    new THREE.Vector2(0.175, 0.145),
    new THREE.Vector2(0.182, 0.2),
    new THREE.Vector2(0.19, 0.222),
    new THREE.Vector2(0.228, 0.226),
    new THREE.Vector2(0.242, 0.21),
    new THREE.Vector2(0.246, 0.14),
  ];
  return new THREE.LatheGeometry(pts, 28);
}

function makeTapWaterTex(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 32;
  c.height = 64;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#2f8fc4';
  ctx.fillRect(0, 0, 32, 64);
  for (let i = 0; i < 7; i++) {
    ctx.fillStyle = i % 2 ? 'rgba(180, 230, 255, 0.55)' : 'rgba(90, 170, 220, 0.4)';
    ctx.fillRect(3 + (i % 3) * 8, i * 9, 7, 12);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(1, 2);
  tex.needsUpdate = true;
  return tex;
}

let _tapWaterMat: THREE.MeshBasicMaterial | null = null;
let _tapPuddleMat: THREE.MeshBasicMaterial | null = null;
function tapWaterMat(): THREE.MeshBasicMaterial {
  if (!_tapWaterMat) {
    _tapWaterMat = new THREE.MeshBasicMaterial({
      map: makeTapWaterTex(),
      color: 0x6ec4e6,
      transparent: true,
      opacity: 0.38,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }
  return _tapWaterMat;
}
function tapPuddleMat(): THREE.MeshBasicMaterial {
  if (!_tapPuddleMat) {
    _tapPuddleMat = new THREE.MeshBasicMaterial({
      color: 0x4aa8d0,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
    });
  }
  return _tapPuddleMat;
}

function addSink(parent: THREE.Object3D, _mats: ArenaMats, x: number, z: number, yaw: number): void {
  const g = new THREE.Group();
  g.name = 'sink';
  g.position.set(x, 0, z);
  g.rotation.y = yaw;
  g.userData.interact = 'sink';
  const porcelain = porcelainMat();
  const chrome = chromeMat();
  const drainMat = SINK_DRAIN;

  const foot = new THREE.Mesh(_unitBox, porcelain);
  foot.position.set(0, 0.04, 0);
  foot.scale.set(0.24, 0.08, 0.2);
  const column = new THREE.Mesh(_unitBox, porcelain);
  column.position.set(0, 0.38, -0.02);
  column.scale.set(0.15, 0.56, 0.14);
  const capital = new THREE.Mesh(_unitBox, porcelain);
  capital.position.set(0, 0.68, 0.0);
  capital.scale.set(0.22, 0.05, 0.2);

  const bowlY = 0.66;
  const bowlZ = 0.1;
  const tapBackZ = -0.1;
  const neckR = (bowlZ - tapBackZ) * 0.5;
  const neckY = 1.04;
  const neckZ = tapBackZ + neckR;
  const aeratorY = 0.96;
  const bowl = new THREE.Mesh(sinkBowlGeo(), porcelain);
  bowl.position.set(0, bowlY, bowlZ);
  const drain = new THREE.Mesh(_unitCyl, drainMat);
  drain.position.set(0, bowlY + 0.008, bowlZ);
  drain.scale.set(0.028, 0.01, 0.028);

  const backDeck = new THREE.Mesh(_unitBox, porcelain);
  backDeck.position.set(0, 0.88, tapBackZ);
  backDeck.scale.set(0.5, 0.055, 0.14);
  const splash = new THREE.Mesh(_unitBox, porcelain);
  splash.position.set(0, 0.98, -0.16);
  splash.scale.set(0.5, 0.16, 0.04);

  const plate = new THREE.Mesh(_unitBox, chrome);
  plate.position.set(0, 0.92, tapBackZ);
  plate.scale.set(0.18, 0.018, 0.08);
  const riser = new THREE.Mesh(_unitCyl, chrome);
  riser.position.set(0, 0.99, tapBackZ);
  riser.scale.set(0.016, 0.1, 0.016);
  const neck = new THREE.Mesh(new THREE.TorusGeometry(neckR, 0.013, 8, 14, Math.PI * 0.92), chrome);
  neck.position.set(0, neckY, neckZ);
  neck.rotation.set(Math.PI * 0.5, 0, Math.PI * 0.5);
  const spout = new THREE.Mesh(_unitCyl, chrome);
  spout.position.set(0, 0.99, bowlZ);
  spout.scale.set(0.012, 0.055, 0.012);
  const aerator = new THREE.Mesh(_unitCyl, chrome);
  aerator.position.set(0, aeratorY, bowlZ);
  aerator.scale.set(0.016, 0.012, 0.016);

  const knobs: THREE.Object3D[] = [];
  for (const side of [-1, 1]) {
    const stem = new THREE.Mesh(_unitCyl, chrome);
    stem.position.set(side * 0.08, 0.96, -0.1);
    stem.scale.set(0.01, 0.055, 0.01);
    const knob = new THREE.Mesh(_unitCyl, chrome);
    knob.position.set(side * 0.08, 0.995, -0.1);
    knob.scale.set(0.022, 0.012, 0.022);
    const cross = new THREE.Mesh(_unitBox, chrome);
    cross.position.set(side * 0.08, 1.008, -0.1);
    cross.scale.set(0.042, 0.008, 0.01);
    knobs.push(knob, cross);
    g.add(stem, knob, cross);
  }

  const trap = new THREE.Mesh(new THREE.TorusGeometry(0.055, 0.012, 8, 14, Math.PI), chrome);
  trap.position.set(0, 0.22, 0.04);
  trap.rotation.x = Math.PI * 0.5;

  const waterMat = tapWaterMat();
  const streamTop = aeratorY - 0.01;
  const streamH = streamTop - (bowlY + 0.02);
  const stream = new THREE.Mesh(new THREE.CylinderGeometry(0.01, 0.016, 1, 8, 1, true), waterMat);
  stream.position.set(0, streamTop - streamH * 0.5, bowlZ);
  stream.scale.set(1, streamH, 1);
  stream.visible = false;
  stream.userData.ignoreHit = true;
  stream.frustumCulled = false;

  const puddle = new THREE.Mesh(new THREE.CircleGeometry(0.09, 16), tapPuddleMat());
  puddle.rotation.x = -Math.PI * 0.5;
  puddle.position.set(0, bowlY + 0.012, bowlZ);
  puddle.visible = false;
  puddle.userData.ignoreHit = true;

  g.userData.stream = stream;
  g.userData.puddle = puddle;
  g.userData.knobs = knobs;

  g.add(
    foot,
    column,
    capital,
    bowl,
    drain,
    backDeck,
    splash,
    plate,
    riser,
    neck,
    spout,
    aerator,
    trap,
    stream,
    puddle,
  );
  g.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh) {
      mesh.castShadow = mesh !== stream && mesh !== puddle;
      mesh.receiveShadow = mesh !== stream && mesh !== puddle;
    }
  });
  parent.add(g);
}

function addMirror(parent: THREE.Object3D, x: number, y: number, z: number): void {
  const glassW = 0.5;
  const glassH = 0.64;
  const frame = MIRROR_FRAME;
  const backing = new THREE.Mesh(_unitBox, frame);
  backing.position.set(x + 0.012, y, z);
  backing.scale.set(0.018, glassH + 0.05, glassW + 0.05);
  backing.castShadow = true;
  parent.add(backing);

  const t = 0.022;
  const edges: Array<[number, number, number, number, number, number]> = [
    [x, y + glassH * 0.5 + t * 0.35, z, 0.028, t, glassW + 0.05],
    [x, y - glassH * 0.5 - t * 0.35, z, 0.028, t, glassW + 0.05],
    [x, y, z + glassW * 0.5 + t * 0.15, 0.028, glassH + 0.05, t],
    [x, y, z - glassW * 0.5 - t * 0.15, 0.028, glassH + 0.05, t],
  ];
  for (const [px, py, pz, sx, sy, sz] of edges) {
    const strip = new THREE.Mesh(_unitBox, frame);
    strip.position.set(px, py, pz);
    strip.scale.set(sx, sy, sz);
    parent.add(strip);
  }

  const hit = new THREE.Mesh(
    new THREE.BoxGeometry(0.02, glassH, glassW),
    new THREE.MeshBasicMaterial({ visible: false }),
  );
  hit.name = 'bath_mirror_hit';
  hit.position.set(x - 0.006, y, z);
  hit.userData.destructible = 'glass';
  hit.userData.hideOnShatter = [backing];

  const rt = mirrorRtSize(_mirrorQuality);
  const glass = new Reflector(new THREE.PlaneGeometry(glassW, glassH), {
    clipBias: 0.003,
    textureWidth: rt,
    textureHeight: rt,
    multisample: 0,
    color: 0xc4d0d6,
  });
  glass.name = 'bath_mirror';
  glass.rotation.y = -Math.PI / 2;
  cheapenMirror(glass, _mirrorStagger * 55);
  _mirrorGlasses.push(glass);
  _mirrorStagger += 1;
  hit.add(glass);
  parent.add(hit);
}

/** Skip recursive pane-in-pane. Crap mode also throttles and keeps one live RT. */
function cheapenMirror(glass: THREE.Object3D, staggerMs: number): void {
  const reflect = glass.onBeforeRender.bind(glass);
  glass.userData.mirrorLast = -staggerMs;
  glass.onBeforeRender = (renderer, scene, camera, geometry, material, group) => {
    if (!_mirrorsLive && !_mirrorForce) return;
    if (_mirrorBusy) return;
    glass.getWorldPosition(_mirrorPos);
    const dx = camera.position.x - _mirrorPos.x;
    const dy = camera.position.y - _mirrorPos.y;
    const dz = camera.position.z - _mirrorPos.z;
    const rangeSq = _mirrorQuality === 'high' ? 100 : 36;
    if (!_mirrorForce && dx * dx + dy * dy + dz * dz > rangeSq) return;
    if (!_mirrorForce) {
      glass.getWorldDirection(_mirrorN);
      if (dx * _mirrorN.x + dy * _mirrorN.y + dz * _mirrorN.z < 0.05) return;
      camera.getWorldDirection(_camFwd);
      if (_camFwd.dot(_mirrorN) > -0.12) return;
      const now = performance.now();
      const interval = _mirrorQuality === 'high' ? 33 : 110;
      if (now - glass.userData.mirrorLast < interval) return;
      if (_mirrorQuality === 'crap' && now - _mirrorLastGlobal < 50) return;
      if (mirrorCameraStill(camera)) return;
      glass.userData.mirrorLast = now;
      _mirrorLastGlobal = now;
    }
    const sm = renderer.shadowMap;
    const prevAuto = sm.autoUpdate;
    const prevNeeds = sm.needsUpdate;
    sm.autoUpdate = false;
    sm.needsUpdate = false;
    _mirrorBusy = true;
    _mirrorScenePrep?.(true);
    try {
      reflect(renderer, scene, camera, geometry, material, group);
      rememberMirrorCamera(camera);
    } finally {
      _mirrorScenePrep?.(false);
      _mirrorBusy = false;
      sm.autoUpdate = prevAuto;
      sm.needsUpdate = prevNeeds;
    }
  };
}

function addBathroom(parent: THREE.Object3D, mats: ArenaMats): void {
  const gap = 0.16;
  const panelH = 1.82;
  const panelY = gap + panelH * 0.5;
  const doorZ = 3.88;
  const frontT = 0.06;
  const sideT = 0.1;
  const postW = 0.055;
  const backZ = 0.32;
  const frontZ = doorZ - 0.035;
  const frontFaceZ = frontZ + frontT * 0.5;
  const sideEndZ = frontFaceZ;
  const sideDepth = sideEndZ - backZ;
  const sideZ = (backZ + sideEndZ) * 0.5;
  const bounds = [-4.7, -2.5, 0, 2.5, 4.7];
  const bays = [-3.75, -1.25, 1.25, 3.75];
  const postOff = 0.5;
  const panels: Array<{ x: number; z: number; sx: number; sz: number }> = [];

  const addPanel = (x: number, z: number, sx: number, sz: number) => {
    if (sx < 0.05 || sz < 0.05) return;
    panels.push({ x, z, sx, sz });
  };

  for (const x of [-2.5, 0, 2.5]) {
    addPanel(x, sideZ, sideT, sideDepth);
  }

  for (let i = 0; i < bays.length; i++) {
    const cx = bays[i];
    addToilet(parent, mats, cx, 1.45);
    for (const side of [-1, 1]) {
      addPanel(cx + side * postOff, frontZ, postW, frontT + 0.01);
    }
    const join = sideT * 0.5 - 0.004;
    const left0 = bounds[i] + (i > 0 ? join : 0);
    const left1 = cx - postOff - postW * 0.5 - 0.01;
    const right0 = cx + postOff + postW * 0.5 + 0.01;
    const right1 = bounds[i + 1] - (i < bays.length - 1 ? join : 0);
    addPanel((left0 + left1) * 0.5, frontZ, left1 - left0, frontT);
    addPanel((right0 + right1) * 0.5, frontZ, right1 - right0, frontT);
  }

  if (panels.length > 0) {
    const batch = new THREE.InstancedMesh(_unitBox, STALL_PANEL, panels.length);
    batch.name = 'stall_panels';
    batch.userData.rooms = ['bath'];
    batch.castShadow = true;
    batch.receiveShadow = true;
    batch.frustumCulled = true;
    for (let i = 0; i < panels.length; i++) {
      const p = panels[i]!;
      _dummy.position.set(p.x, panelY, p.z);
      _dummy.rotation.set(0, 0, 0);
      _dummy.scale.set(p.sx, panelH, p.sz);
      _dummy.updateMatrix();
      batch.setMatrixAt(i, _dummy.matrix);
    }
    batch.instanceMatrix.needsUpdate = true;
    parent.add(batch);
  }
  addSink(parent, mats, 4.55, 7.4, -Math.PI * 0.5);
  addSink(parent, mats, 4.55, 9.3, -Math.PI * 0.5);
  addMirror(parent, 4.78, 1.58, 7.4);
  addMirror(parent, 4.78, 1.58, 9.3);

  // Shared door wall is foyer plaster on both sides — tile only the WC face.
  const tile = mats.wallBath;
  const doorHalf = 0.64;
  const innerZ = 10.78;
  const tileH = 3.0;
  const tileY = tileH * 0.5;
  const addFaceTile = (x: number, y: number, sx: number, sy: number) => {
    const geo = new THREE.BoxGeometry(sx, sy, 0.04);
    geo.userData.bakeOwned = true;
    const slab = new THREE.Mesh(geo, tile);
    slab.position.set(x, y, innerZ);
    slab.receiveShadow = true;
    applyWorldWallUvs(slab, 'x');
    parent.add(slab);
  };
  addFaceTile(-2.82, tileY, 4.36, tileH);
  addFaceTile(2.82, tileY, 4.36, tileH);
  addFaceTile(0, 2.64, doorHalf * 2, 0.72);
}

function addNeonBar(
  parent: THREE.Object3D,
  x: number,
  y: number,
  z: number,
  sx: number,
  sy: number,
  sz: number,
  color: number,
  light = true,
): BakeLight | null {
  const mat = new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: 3.4,
    roughness: 0.22,
    metalness: 0.08,
  });
  const bar = new THREE.Mesh(_unitBox, mat);
  bar.position.set(x, y, z);
  bar.scale.set(sx, sy, sz);
  bar.castShadow = false;
  bar.receiveShadow = false;
  parent.add(bar);
  if (!light) return null;
  return {
    position: new THREE.Vector3(x, y - 0.15, z),
    color: new THREE.Color(color),
    intensity: 0.55,
    range: 6.2,
  };
}

function addFoyerNeon(parent: THREE.Object3D): BakeLight[] {
  const y = 2.58;
  const cyan = 0x3ad4ff;
  const gold = 0xffc44a;
  const bake: BakeLight[] = [];
  const push = (L: BakeLight | null) => {
    if (L) bake.push(L);
  };
  push(addNeonBar(parent, 5.72, y, 16.1, 0.045, 0.055, 8.6, cyan));
  push(addNeonBar(parent, -5.72, y, 13.15, 0.045, 0.055, 3.4, gold));
  push(addNeonBar(parent, -5.72, y, 18.85, 0.045, 0.055, 3.4, gold));
  addNeonBar(parent, -3.35, y, 20.72, 3.9, 0.055, 0.045, cyan, false);
  addNeonBar(parent, 3.35, y, 20.72, 3.9, 0.055, 0.045, cyan, false);
  addNeonBar(parent, -3.2, y, 11.28, 3.2, 0.055, 0.045, gold, false);
  addNeonBar(parent, 3.2, y, 11.28, 3.2, 0.055, 0.045, gold, false);
  bake.push({
    position: new THREE.Vector3(0, 2.35, 16),
    color: new THREE.Color(0xffe6b0),
    intensity: 0.7,
    range: 8,
  });
  return bake;
}

function addMonitor(parent: THREE.Object3D, mats: ArenaMats, x: number, y: number, z: number, yaw: number): void {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  g.rotation.y = yaw;
  const body = new THREE.Mesh(_unitBox, mats.plastic);
  body.scale.set(0.46, 0.34, 0.06);
  body.position.set(0, 0.2, 0);
  const screen = new THREE.Mesh(_unitBox, mats.screen);
  screen.scale.set(0.4, 0.28, 0.02);
  screen.position.set(0, 0.2, 0.035);
  const stand = new THREE.Mesh(_unitBox, mats.plastic);
  stand.scale.set(0.08, 0.14, 0.08);
  stand.position.set(0, 0.02, 0);
  g.add(body, screen, stand);
  parent.add(g);
}

function addKeyboard(parent: THREE.Object3D, mats: ArenaMats, x: number, y: number, z: number): void {
  const m = new THREE.Mesh(_unitBox, mats.plastic);
  m.position.set(x, y, z);
  m.scale.set(0.38, 0.025, 0.14);
  parent.add(m);
}

function addPapers(
  parent: THREE.Object3D,
  mats: ArenaMats,
  x: number,
  y: number,
  z: number,
  yaw: number,
  onCrate = false,
): void {
  const m = new THREE.Mesh(_unitBox, mats.paper);
  m.position.set(x, y, z);
  m.rotation.y = yaw;
  m.scale.set(0.22, 0.012, 0.28);
  if (onCrate) {
    m.userData.looseProp = 'paper';
    m.userData.ignoreHit = true;
  }
  parent.add(m);
}

function addDeskLamp(parent: THREE.Object3D, mats: ArenaMats, x: number, y: number, z: number): void {
  const arm = new THREE.Mesh(_unitCyl, mats.metal);
  arm.position.set(x, y + 0.12, z);
  arm.scale.set(0.012, 0.24, 0.012);
  const shade = new THREE.Mesh(_unitCyl, mats.plastic);
  shade.position.set(x, y + 0.26, z);
  shade.scale.set(0.07, 0.05, 0.07);
  parent.add(arm, shade);
}

function addDesks(parent: THREE.Object3D, mats: ArenaMats): void {
  const top0 = 0.55 + 0.425;
  const topU = YU + top0;

  addMonitor(parent, mats, -4.45, top0, 26.7, 0);
  addKeyboard(parent, mats, -4.3, top0 + 0.02, 27.05);
  addPapers(parent, mats, -2.55, top0 + 0.01, 26.65, 0.2);
  addDeskLamp(parent, mats, -2.4, top0, 27.28);

  addMonitor(parent, mats, -5.9, top0, 41.7, 0);
  addKeyboard(parent, mats, -5.5, top0 + 0.02, 42.05);
  addPapers(parent, mats, -4.4, top0 + 0.01, 41.7, -0.15);
  addDeskLamp(parent, mats, -4.2, top0, 42.25);

  addMonitor(parent, mats, 5.85, top0, 41.7, 0);
  addKeyboard(parent, mats, 5.55, top0 + 0.02, 42.05);
  addPapers(parent, mats, 5.35, top0 + 0.01, 41.68, 0.1);
  addDeskLamp(parent, mats, 5.9, top0, 42.22);

  addMonitor(parent, mats, 14.7, top0, 24.2, 0);
  addKeyboard(parent, mats, 15.2, top0 + 0.02, 24.55);
  addPapers(parent, mats, 16.3, top0 + 0.01, 24.25, 0.18);
  addDeskLamp(parent, mats, 16.4, top0, 24.75);

  addMonitor(parent, mats, 25.7, topU, 38.2, 0);
  addKeyboard(parent, mats, 26.2, topU + 0.02, 38.55);
  addPapers(parent, mats, 27.2, topU + 0.01, 38.25, 0.25);
  addDeskLamp(parent, mats, 27.3, topU, 38.75);

  addMonitor(parent, mats, 27.7, topU, 45.2, 0);
  addKeyboard(parent, mats, 28.2, topU + 0.02, 45.55);
  addPapers(parent, mats, 29.2, topU + 0.01, 45.25, -0.2);

  addMonitor(parent, mats, 27.7, topU, 52.5, 0);
  addKeyboard(parent, mats, 28.2, topU + 0.02, 52.85);
  addPapers(parent, mats, 29.2, topU + 0.01, 52.55, 0.15);
  addDeskLamp(parent, mats, 29.3, topU, 53.05);
}

function addBottleRack(parent: THREE.Object3D, mats: ArenaMats, x: number, y: number, z: number): void {
  const rack = new THREE.Mesh(_unitBox, mats.metal);
  rack.position.set(x, y + 0.08, z);
  rack.scale.set(0.55, 0.16, 0.22);
  parent.add(rack);
  for (let i = -1; i <= 1; i++) {
    const bot = new THREE.Mesh(_unitCyl, mats.screen);
    bot.position.set(x + i * 0.14, y + 0.2, z);
    bot.scale.set(0.035, 0.16, 0.035);
    parent.add(bot);
  }
}

function addWallCabinet(
  parent: THREE.Object3D,
  mats: ArenaMats,
  x: number,
  y: number,
  z: number,
  yaw: number,
  mat?: THREE.Material,
): void {
  const body = new THREE.Mesh(_unitBox, mat ?? mats.metal);
  body.position.set(x, y, z);
  body.rotation.y = yaw;
  body.scale.set(1.1, 0.85, 0.28);
  body.castShadow = true;
  parent.add(body);
}

function addLabDressing(parent: THREE.Object3D, mats: ArenaMats): void {
  addWallCabinet(parent, mats, 31.72, YU + 1.55, 38.5, -Math.PI * 0.5);
  addWallCabinet(parent, mats, 31.72, YU + 1.55, 45.8, -Math.PI * 0.5);
  addWallCabinet(parent, mats, 31.72, YU + 1.55, 53.2, -Math.PI * 0.5);
  const top = YU + 0.55 + 0.425;
  addBottleRack(parent, mats, 27.3, top, 38.7);
  addBottleRack(parent, mats, 29.3, top, 45.7);
  addBottleRack(parent, mats, 29.3, top, 53.0);
}

function addRailRing(parent: THREE.Object3D, mats: ArenaMats, cx: number, cz: number, r: number): void {
  const posts = 6;
  for (let i = 0; i < posts; i++) {
    const a0 = (i / posts) * Math.PI * 2;
    const a1 = ((i + 1) / posts) * Math.PI * 2;
    const x0 = cx + Math.cos(a0) * r;
    const z0 = cz + Math.sin(a0) * r;
    const x1 = cx + Math.cos(a1) * r;
    const z1 = cz + Math.sin(a1) * r;
    const post = new THREE.Mesh(_unitCyl, mats.metal);
    post.position.set(x0, 0.55, z0);
    post.scale.set(0.035, 1.1, 0.035);
    parent.add(post);
    addPipe(parent, mats.pipe, x0, 1.05, z0, x1, 1.05, z1, 0.022);
  }
}

function addConveyor(parent: THREE.Object3D, mats: ArenaMats, x: number, z0: number, z1: number): void {
  const z = (z0 + z1) * 0.5;
  const len = z1 - z0;
  const bed = new THREE.Mesh(_unitBox, mats.crate);
  bed.position.set(x, 0.28, z);
  bed.scale.set(0.72, 0.16, len);
  bed.receiveShadow = true;
  parent.add(bed);
  for (const side of [-1, 1] as const) {
    const rail = new THREE.Mesh(_unitBox, mats.metal);
    rail.position.set(x + side * 0.34, 0.42, z);
    rail.scale.set(0.04, 0.12, len);
    parent.add(rail);
  }
}

function addBottling(parent: THREE.Object3D, mats: ArenaMats): void {
  addConveyor(parent, mats, -9.6, 58, 68);
  addConveyor(parent, mats, 9.6, 56, 66);
  addRailRing(parent, mats, -5, 62, 1.28);
  addRailRing(parent, mats, 5, 62, 1.28);
  addRailRing(parent, mats, 0, 69.5, 1.18);
}

function addStackedCrate(
  parent: THREE.Object3D,
  mats: ArenaMats,
  x: number,
  topY: number,
  z: number,
  sx: number,
  sy: number,
  sz: number,
  yaw = 0.12,
  style: CrateStyle = 'mil',
): void {
  const m = new THREE.Mesh(_unitBox, style === 'wood' ? mats.crateWood : mats.crateMil);
  m.name = 'crate_stack';
  m.position.set(x + 0.08, topY + sy * 0.5, z - 0.05);
  m.rotation.y = yaw;
  m.scale.set(sx, sy, sz);
  m.castShadow = true;
  m.receiveShadow = true;
  m.userData.destructible = 'crate';
  m.userData.crateStyle = style;
  parent.add(m);
}

function addCrateStacks(parent: THREE.Object3D, mats: ArenaMats): void {
  addStackedCrate(parent, mats, 5.5, 0.9, 24.2, 0.9, 0.5, 0.8, -0.1, 'mil');
  addStackedCrate(parent, mats, 11.2, 0.9, 32.5, 0.85, 0.45, 0.7, 0.14, 'wood');
  addStackedCrate(parent, mats, -19.5, YB + 1.0, 24.5, 1.0, 0.5, 0.85, -0.15, 'mil');
  addStackedCrate(parent, mats, 18.2, YU + 0.8, 38.2, 0.8, 0.4, 0.7, 0.08, 'wood');
  addStackedCrate(parent, mats, -9.2, 1.0, 55.5, 1.1, 0.5, 0.9, 0.2, 'mil');
  addStackedCrate(parent, mats, 8.2, 1.0, 71.5, 1.05, 0.48, 0.85, -0.18, 'wood');
  addStackedCrate(parent, mats, -8.2, 0.9, 48.2, 0.85, 0.42, 0.72, 0.16, 'wood');
  addStackedCrate(parent, mats, 18.6, 0.0, 33.4, 0.72, 0.4, 0.62, -0.14, 'mil');
}

function addKitchenDressing(parent: THREE.Object3D, mats: ArenaMats): void {
  const counter = new THREE.Mesh(_unitBox, mats.desk);
  counter.position.set(30.6, 0.48, 28);
  counter.scale.set(1.4, 0.96, 8.5);
  counter.castShadow = true;
  counter.receiveShadow = true;
  parent.add(counter);
  const trayMat = cloneMatWorldUv(mats.tankMetal, 1.2, 0.8);
  const addTray = (x: number, y: number, z: number, sx: number, sz: number) => {
    const plate = new THREE.Mesh(_unitBox, trayMat);
    plate.position.set(x, y, z);
    plate.scale.set(sx, 0.03, sz);
    parent.add(plate);
  };
  for (const z of [26.2, 28.8, 31.4]) addTray(30.55, 0.98, z, 0.62, 0.42);
  const table = new THREE.Mesh(_unitBox, mats.desk);
  table.position.set(24.2, 0.4, 26.5);
  table.scale.set(1.8, 0.08, 1.1);
  table.castShadow = true;
  parent.add(table);
  for (const [x, z] of [
    [23.5, 26.0],
    [24.9, 26.0],
    [23.5, 27.0],
    [24.9, 27.0],
  ] as const) {
    const leg = new THREE.Mesh(_unitCyl, mats.tankMetal);
    leg.position.set(x, 0.2, z);
    leg.scale.set(0.035, 0.4, 0.035);
    parent.add(leg);
  }
  addTray(23.55, 0.46, 26.25, 0.42, 0.32);
  addTray(24.75, 0.46, 26.55, 0.38, 0.3);
  addPapers(parent, mats, 24.0, 0.46, 26.3, 0.2);
  addStackedCrate(parent, mats, 22.4, 0.0, 33.2, 0.7, 0.4, 0.6, 0.1, 'mil');
}

function addGeneratorDressing(parent: THREE.Object3D, mats: ArenaMats): void {
  for (const [x, z] of [
    [24.2, 40.2],
    [28.8, 46.4],
  ] as const) {
    const body = new THREE.Mesh(_unitBox, mats.metal);
    body.position.set(x, 0.85, z);
    body.scale.set(2.2, 1.7, 1.4);
    body.castShadow = true;
    body.receiveShadow = true;
    parent.add(body);
    const band = new THREE.Mesh(_unitBox, mats.hazard);
    band.position.set(x, 0.85, z);
    band.scale.set(2.24, 0.16, 1.44);
    parent.add(band);
    const vent = new THREE.Mesh(_unitCyl, mats.pipe);
    vent.position.set(x + 0.7, 1.55, z);
    vent.scale.set(0.22, 0.35, 0.22);
    parent.add(vent);
  }
  addStripe(parent, mats, 26.5, 43, 0.28, 10);
  addStackedCrate(parent, mats, 30.4, 0.0, 38.8, 0.75, 0.42, 0.65, -0.12, 'wood');
  addPipe(parent, mats.pipe, 24.9, 1.72, 40.2, 24.9, 2.88, 40.2, 0.06);
  addPipe(parent, mats.pipe, 29.5, 1.72, 46.4, 29.5, 2.88, 46.4, 0.06);
}

function addStashDressing(parent: THREE.Object3D, mats: ArenaMats): void {
  addStackedCrate(parent, mats, -8.3, YB + 1.0, 5.4, 0.85, 0.42, 0.7, 0.16, 'wood');
  addStackedCrate(parent, mats, -19.7, YB + 1.0, 16.5, 0.8, 0.4, 0.65, -0.12, 'wood');
  addStackedCrate(parent, mats, -20.0, YB + 0.0, 9.4, 0.7, 0.38, 0.6, 0.2, 'mil');
  addStackedCrate(parent, mats, -14.2, YB + 1.0, 8.8, 0.75, 0.4, 0.62, -0.2, 'mil');
  addPapers(parent, mats, -13.2, YB + 0.96, 4.7, 0.3, true);
  addWallCabinet(parent, mats, -6.28, YB + 1.45, 6.8, -Math.PI * 0.5);
  addPipe(parent, mats.pipe, -15.2, YB + 2.72, 4.0, -7.2, YB + 2.72, 4.0, 0.045);
  addHanger(parent, mats.metal, -13.2, YB + 2.84, 4.0);
  addHanger(parent, mats.metal, -9.2, YB + 2.84, 4.0);
}

function addBench(
  parent: THREE.Object3D,
  mats: ArenaMats,
  x: number,
  y: number,
  z: number,
  sx: number,
  sz: number,
): void {
  const top = new THREE.Mesh(_unitBox, mats.desk);
  top.position.set(x, y + 0.22, z);
  top.scale.set(sx, 0.06, sz);
  top.castShadow = true;
  top.receiveShadow = true;
  parent.add(top);
  for (const [lx, lz] of [
    [-sx * 0.4, -sz * 0.35],
    [sx * 0.4, -sz * 0.35],
    [-sx * 0.4, sz * 0.35],
    [sx * 0.4, sz * 0.35],
  ] as const) {
    const leg = new THREE.Mesh(_unitCyl, mats.tankMetal);
    leg.position.set(x + lx, y + 0.11, z + lz);
    leg.scale.set(0.03, 0.22, 0.03);
    parent.add(leg);
  }
}

function addLockerDressing(parent: THREE.Object3D, mats: ArenaMats): void {
  addBench(parent, mats, 18.4, 0, 39.15, 3.15, 0.42);
  addBench(parent, mats, 18.4, 0, 47.0, 3.15, 0.42);
  addStripe(parent, mats, 18.4, 38.4, 3.5, 0.22);
  addStripe(parent, mats, 18.4, 47.85, 3.5, 0.22);
  addPipe(parent, mats.pipe, 16.4, 2.72, 36.35, 20.5, 2.72, 36.35, 0.04);
  addHanger(parent, mats.metal, 18.4, 2.84, 36.35);
  addPipe(parent, mats.pipe, 16.4, 2.72, 49.85, 20.5, 2.72, 49.85, 0.04);
  addHanger(parent, mats.metal, 18.4, 2.84, 49.85);
}

function addAlarmButtons(parent: THREE.Object3D, mats: ArenaMats): void {
  // Local +Z is the panel face / stand-off. Yaw so that points into the room.
  addAlarmButton(parent, mats, -5.72, 1.28, 14.2, Math.PI * 0.5, 0);
  addAlarmButton(parent, mats, -8.72, 1.28, 24.4, Math.PI * 0.5, 0);
  addAlarmButton(parent, mats, -7.4, 1.28, 50.72, Math.PI, 0);
  addAlarmButton(parent, mats, 31.72, 1.28, 22.2, -Math.PI * 0.5, 0);
  addAlarmButton(parent, mats, -9.28, YB + 1.28, 32.2, -Math.PI * 0.5, YB);
  addAlarmButton(parent, mats, 26.2, YU + 1.28, 35.28, 0, YU);
}

function addAlarmButton(
  parent: THREE.Object3D,
  mats: ArenaMats,
  x: number,
  y: number,
  z: number,
  yaw: number,
  floorY: number,
): void {
  const g = new THREE.Group();
  g.name = 'alarm_button';
  g.position.set(x, y, z);
  g.rotation.y = yaw;
  const stand = new THREE.Vector3(0, 0, 0.82);
  stand.applyAxisAngle(_up, yaw);
  g.userData.interact = 'alarm';
  g.userData.destructible = 'alarm';
  g.userData.alarmStand = new THREE.Vector3(x + stand.x, floorY + 0.05, z + stand.z);

  const plate = new THREE.Mesh(_unitBox, cloneMatWorldUv(mats.metal, 0.4, 0.5));
  plate.scale.set(0.22, 0.32, 0.04);
  plate.position.z = 0.02;
  plate.castShadow = true;
  g.add(plate);

  const bezel = new THREE.Mesh(_unitBox, mats.hazardBlack);
  bezel.scale.set(0.14, 0.14, 0.03);
  bezel.position.set(0, 0.02, 0.045);
  g.add(bezel);

  const knob = new THREE.Mesh(
    new THREE.SphereGeometry(0.045, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.62),
    new THREE.MeshStandardMaterial({
      color: 0xa01810,
      roughness: 0.38,
      metalness: 0.22,
      emissive: 0x3a0404,
      emissiveIntensity: 0.25,
    }),
  );
  knob.rotation.x = Math.PI;
  knob.position.set(0, 0.02, 0.062);
  g.add(knob);
  g.userData.alarmKnob = knob;

  const stripe = new THREE.Mesh(_unitBox, mats.hazard);
  stripe.scale.set(0.2, 0.035, 0.012);
  stripe.position.set(0, -0.12, 0.042);
  g.add(stripe);

  parent.add(g);
}
